import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiEdit2, FiCheck, FiX, FiDollarSign } from 'react-icons/fi';

const CURRENCIES = ['USD', 'EUR', 'TRY', 'AED', 'SAR', 'GBP', 'JPY', 'CNY', 'INR'];

const formatMoney = (amount, currency = 'USD') => {
  const v = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(v);
  } catch (_e) {
    return `${v.toFixed(2)} ${currency}`;
  }
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const PaymentsSection = ({ taskId, token, taskCurrency = 'USD', taskValue = 0, boardBaseCurrency = 'USD', onChanged }) => {
  const [payments, setPayments] = useState([]);
  const [totals, setTotals] = useState({ totalPaid: 0, remaining: 0 });
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ amount: '', currency: taskCurrency, exchangeRate: 1, date: todayStr(), note: '' });

  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const load = async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/items/${taskId}/payments`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPayments(data.payments || []);
        setTotals({ totalPaid: data.totalPaid || 0, remaining: data.remaining || 0 });
      }
    } catch (e) {
      console.error('Failed to load payments:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [taskId]);

  useEffect(() => {
    setForm((f) => ({ ...f, currency: taskCurrency }));
  }, [taskCurrency]);

  const resetForm = () => setForm({ amount: '', currency: taskCurrency, exchangeRate: 1, date: todayStr(), note: '' });

  const handleAdd = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return;
    try {
      const res = await fetch(`${apiBase}/items/${taskId}/payments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemId: taskId,
          amount,
          currency: form.currency,
          exchangeRate: parseFloat(form.exchangeRate) || 1,
          date: form.date || todayStr(),
          note: form.note || '',
        }),
      });
      if (res.ok) {
        resetForm();
        setShowAdd(false);
        await load();
        if (onChanged) onChanged();
      }
    } catch (e) {
      console.error('Failed to add payment:', e);
    }
  };

  const handleUpdate = async (id) => {
    try {
      const res = await fetch(`${apiBase}/payments/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(form.amount) || 0,
          currency: form.currency,
          exchangeRate: parseFloat(form.exchangeRate) || 1,
          date: form.date,
          note: form.note,
        }),
      });
      if (res.ok) {
        setEditingId(null);
        resetForm();
        await load();
        if (onChanged) onChanged();
      }
    } catch (e) {
      console.error('Failed to update payment:', e);
    }
  };

  const handleDelete = async (id, force = false) => {
    if (!force && !window.confirm('Delete this client-visible contractor payment?')) return;
    try {
      const url = `${apiBase}/payments/${id}${force ? '?force=true' : ''}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        const detail = body.detail || {};
        const message = detail.message || 'This deletion would also remove a linked Finance payment with a real internal cost.';
        const actual = detail.actualAmount;
        const confirmText =
          `${message}\n\n` +
          (actual ? `Internal actual amount that will be lost: ${actual}\n\n` : '') +
          'Are you sure you want to permanently delete both?';
        if (window.confirm(confirmText)) {
          return handleDelete(id, true);
        }
        return;
      }
      if (res.ok) {
        await load();
        if (onChanged) onChanged();
      } else {
        const body = await res.json().catch(() => ({}));
        alert((body.detail && (body.detail.message || body.detail)) || 'Failed to delete payment');
      }
    } catch (e) {
      console.error('Failed to delete payment:', e);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      amount: p.amount,
      currency: p.currency || 'USD',
      exchangeRate: p.exchangeRate || 1,
      date: p.date || todayStr(),
      note: p.note || '',
    });
    setShowAdd(false);
  };

  const value = Number(taskValue || 0);
  const percentPaid = value > 0 ? Math.min(100, (totals.totalPaid / value) * 100) : 0;

  return (
    <div className="payments-section" data-testid="payments-section">
      <div className="payments-summary">
        <div className="payments-summary-row">
          <div className="payments-summary-item">
            <div className="payments-summary-label">Value</div>
            <div className="payments-summary-value">{formatMoney(value, taskCurrency)}</div>
          </div>
          <div className="payments-summary-item">
            <div className="payments-summary-label">Paid</div>
            <div className="payments-summary-value" style={{ color: '#1DBF73' }}>
              {formatMoney(totals.totalPaid, taskCurrency)}
            </div>
          </div>
          <div className="payments-summary-item">
            <div className="payments-summary-label">Remaining</div>
            <div className="payments-summary-value" style={{ color: totals.remaining > 0 ? '#E24B5B' : '#1DBF73' }}>
              {formatMoney(totals.remaining, taskCurrency)}
            </div>
          </div>
        </div>
        <div className="payments-progress-track">
          <div className="payments-progress-fill" style={{ width: `${percentPaid}%` }} />
          <span className="payments-progress-text">{percentPaid.toFixed(0)}% paid</span>
        </div>
      </div>

      <div className="payments-list-header">
        <div className="payments-section-title">
          <FiDollarSign size={16} />
          <span>Client-visible Contractor Payments ({payments.length})</span>
        </div>
        {!showAdd && editingId === null && (
          <button className="btn btn-sm btn-primary" onClick={() => { setShowAdd(true); resetForm(); }} data-testid="add-payment-btn">
            <FiPlus size={14} /> Add Client-visible Contractor Payment
          </button>
        )}
      </div>

      {showAdd && (
        <div className="payment-form" data-testid="payment-form-add">
          <div className="payment-form-grid">
            <div>
              <label className="form-label">Amount</label>
              <input type="number" step="0.01" className="input" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00" data-testid="payment-amount-input"
              />
            </div>
            <div>
              <label className="form-label">Currency</label>
              <select className="input" value={form.currency}
                onChange={(e) => {
                  const next = e.target.value;
                  setForm({ ...form, currency: next, exchangeRate: next === boardBaseCurrency ? 1 : form.exchangeRate });
                }}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                {!CURRENCIES.includes(form.currency) && <option value={form.currency}>{form.currency}</option>}
              </select>
            </div>
            <div>
              <label className="form-label">
                {form.currency === boardBaseCurrency
                  ? 'Rate (same)'
                  : `1 ${boardBaseCurrency} = ? ${form.currency}`}
              </label>
              <input type="number" step="0.0001" className="input" value={form.exchangeRate}
                onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })}
                disabled={form.currency === boardBaseCurrency}
              />
            </div>
            <div>
              <label className="form-label">Date</label>
              <input type="date" className="input" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
          </div>
          {form.amount > 0 && form.exchangeRate > 0 && form.currency !== boardBaseCurrency && (
            <p className="form-helper" style={{ marginTop: -8 }}>
              ≈ <strong>{(parseFloat(form.amount) / parseFloat(form.exchangeRate)).toLocaleString(undefined, { maximumFractionDigits: 2 })} {boardBaseCurrency}</strong>
            </p>
          )}
          <div className="form-group">
            <label className="form-label">Note (optional)</label>
            <input type="text" className="input" value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g. Invoice #123, milestone payment"
            />
          </div>
          <div className="payment-form-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowAdd(false); resetForm(); }}>
              <FiX size={14} /> Cancel
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleAdd} data-testid="save-payment-btn">
              <FiCheck size={14} /> Save Payment
            </button>
          </div>
        </div>
      )}

      <div className="payments-list">
        {loading && <div className="empty-state">Loading...</div>}
        {!loading && payments.length === 0 && !showAdd && (
          <div className="empty-state">No client-visible contractor payments yet. Click "Add Client-visible Contractor Payment" to record the first one.</div>
        )}
        {payments.map((p) => (
          <div key={p.id} className="payment-row" data-testid="payment-row">
            {editingId === p.id ? (
              <div className="payment-form" style={{ width: '100%' }}>
                <div className="payment-form-grid">
                  <div>
                    <label className="form-label">Amount</label>
                    <input type="number" step="0.01" className="input" value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label">Currency</label>
                    <select className="input" value={form.currency}
                      onChange={(e) => {
                        const next = e.target.value;
                        setForm({ ...form, currency: next, exchangeRate: next === boardBaseCurrency ? 1 : form.exchangeRate });
                      }}>
                      {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">
                      {form.currency === boardBaseCurrency ? 'Rate (same)' : `1 ${boardBaseCurrency} = ? ${form.currency}`}
                    </label>
                    <input type="number" step="0.0001" className="input" value={form.exchangeRate}
                      onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })}
                      disabled={form.currency === boardBaseCurrency}
                    />
                  </div>
                  <div>
                    <label className="form-label">Date</label>
                    <input type="date" className="input" value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Note</label>
                  <input type="text" className="input" value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                  />
                </div>
                <div className="payment-form-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditingId(null); resetForm(); }}>
                    <FiX size={14} /> Cancel
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => handleUpdate(p.id)}>
                    <FiCheck size={14} /> Update
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="payment-row-main">
                  <div className="payment-row-amount">
                    {formatMoney(p.amount, p.currency)}
                  </div>
                  <div className="payment-row-date">{p.date}</div>
                </div>
                {p.note && <div className="payment-row-note">{p.note}</div>}
                <div className="payment-row-actions">
                  <button className="row-action-btn" onClick={() => startEdit(p)} aria-label="Edit payment">
                    <FiEdit2 size={14} />
                  </button>
                  <button className="row-action-btn" onClick={() => handleDelete(p.id)} aria-label="Delete payment">
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentsSection;
